import { fileTypeFromBlob } from 'file-type'
import {
  BOOLEAN_FILTER_OPERATORS,
  DATE_FILTER_OPERATORS,
  DATE_TIME_FILTER_OPERATORS,
  DROPDOWN_FILTER_OPERATORS,
  FILE_EXTENSIONS,
  FILE_NAME_VALIDATION_ERRORS,
  FileType,
  HTML_TYPE,
  MAX_FILE_NAME_LENGTH,
  NUMERIC_FILTER_OPERATORS,
  STRING_FILTER_OPERATORS,
  TIME_FILTER_OPERATORS,
  filterOperatorValues,
  tableFilterType,
} from './constants'
import {
  allowedFilenameRegex,
  ValidateFileNameResultType,
} from './types/fileUploadTypes'
import { FilterOperatorsType } from './types/tableFilterTypes'
import { NAMESPACE } from './strings'
import axios from 'axios'

export const getFilterOperators = (dataType?: string) => {
  switch (dataType) {
    case tableFilterType.STRING:
    case tableFilterType.STRING_DYNAMIC:
      return STRING_FILTER_OPERATORS
    case tableFilterType.NUMBER:
      return NUMERIC_FILTER_OPERATORS
    case tableFilterType.DATE:
      return DATE_FILTER_OPERATORS
    case tableFilterType.DATE_TIME_DYNAMIC:
    case tableFilterType.DATE_TIME:
      return DATE_TIME_FILTER_OPERATORS
    case tableFilterType.TIME:
      return TIME_FILTER_OPERATORS
    case tableFilterType.BOOLEAN:
      return BOOLEAN_FILTER_OPERATORS
    case tableFilterType.DROPDOWN:
    case tableFilterType.MULTI_SELECT_DYNAMIC:
      return DROPDOWN_FILTER_OPERATORS
    default:
      return []
  }
}

export const getInitialFilterOperator = (
  dataType?: string,
  operator?: string
) => {
  let operators: FilterOperatorsType[] = []
  switch (dataType) {
    case tableFilterType.STRING:
    case tableFilterType.STRING_DYNAMIC:
      operators = STRING_FILTER_OPERATORS
      break
    case tableFilterType.NUMBER:
      operators = NUMERIC_FILTER_OPERATORS
      break
    case tableFilterType.DATE:
      operators = DATE_FILTER_OPERATORS
      break
    case tableFilterType.DATE_TIME_DYNAMIC:
    case tableFilterType.DATE_TIME:
      operators = DATE_TIME_FILTER_OPERATORS
      break
    case tableFilterType.TIME:
      operators = TIME_FILTER_OPERATORS
      break
    case tableFilterType.BOOLEAN:
      operators = BOOLEAN_FILTER_OPERATORS
      break
    case tableFilterType.DROPDOWN:
    case tableFilterType.MULTI_SELECT_DYNAMIC:
      operators = DROPDOWN_FILTER_OPERATORS
      break
    default:
      operators = []
  }
  return operator
    ? operators.find((op) => op.value === operator) ?? operators?.[0]
    : operators?.[0] ?? null
}

export const getMinutesFromTime = (time: string): number => {
  const [hours, minutes] = time.split(':')
  return parseInt(hours) * 60 + parseInt(minutes)
}

export const addOneMinute = (time: string): string => {
  let [hours, minutes, seconds] = time.split(':').map((val) => parseInt(val))
  seconds = Number(seconds)
  minutes = minutes + 1
  if (minutes === 60) {
    minutes = 0
    hours = hours + 1
  }
  if (hours === 24) {
    hours = 0
  }
  return `${hours > 9 ? hours : '0' + hours}:${
    minutes > 9 ? minutes : '0' + minutes
  }:${seconds > 9 ? seconds : '0' + seconds}`
}

export const subtractOneMinute = (time: string): string => {
  let [hours, minutes, seconds] = time.split(':').map((val) => parseInt(val))
  seconds = Number(seconds)
  minutes = minutes - 1
  if (minutes === -1) {
    minutes = 59
    hours = hours - 1
  }
  if (hours === -1) {
    hours = 0
  }
  return `${hours > 9 ? hours : '0' + hours}:${
    minutes > 9 ? minutes : '0' + minutes
  }:${seconds > 9 ? seconds : '0' + seconds}`
}

export const openNewWindow = (path: string) => {
  window.open(`${path}`, '_blank')
}

export const patternMatcher = (pattern: string, pathname: string) => {
  const patternSegments = pattern.split('/').filter(Boolean)
  const pathSegments = pathname.split('/').filter(Boolean)

  if (patternSegments.length !== pathSegments.length) {
    return false
  }

  for (let i = 0; i < patternSegments.length; i++) {
    const patternSegment = patternSegments[i]
    const pathSegment = pathSegments[i]

    if (
      (patternSegment.startsWith(':') && !/^\d+$/.test(pathSegment)) ||
      (!patternSegment.startsWith(':') && patternSegment !== pathSegment)
    ) {
      return false
    }
  }

  return true
}

export const urlPatternMatcher = (
  path: string,
  config: { url: string[]; message: string }[]
) => {
  for (const configItem of config) {
    for (const urlPattern of configItem.url) {
      if (patternMatcher(urlPattern, path)) {
        return configItem
      }
    }
  }
  return null
}

export const swapArialFont = (htmlContent: string) => {
  return htmlContent.replace(
    /font-family\s*:\s*["']?Arial["']?/gi,
    'font-family: Arial, Helvetica, sans-serif'
  )
}

export const getExtensionFromBlob = (blob: Blob): string => {
  const mimeType = blob.type
  const mimeToExt: Record<string, string> = {
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'docx',
    'application/vnd.ms-word.document.macroEnabled.12': 'docm',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel.sheet.macroEnabled.12': 'xlsm',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'text/plain': 'txt',
    'text/csv': 'csv',
    'message/rfc822': 'eml',
    'application/zip': 'zip',
  }
  return mimeToExt[mimeType] || 'pdf' // fallback to .pdf if unknown
}

export const getFileType = (filename: string): FileType => {
  const ext = filename?.split('.')?.pop()?.toLowerCase()

  if (!ext) return FileType.OTHER

  switch (ext) {
    case 'pdf':
      return FileType.PDF
    case 'doc':
    case 'docx':
      return FileType.WORD
    case 'xls':
    case 'xlsx':
      return FileType.EXCEL
    case 'jpg':
    case 'jpeg':
    case 'png':
      return FileType.IMAGE
    default:
      return FileType.OTHER
  }
}

export const isWordOrExcelOrCsvDocument = (filename: string): boolean => {
  return /\.(docx?|xlsx?|csv)$/i.test(filename)
}

export const getHtmlDataType = (data?: string): string => {
  if (!data || typeof data !== 'string') return HTML_TYPE.UNKNOWN

  const trimmedData = data.trim()

  if (trimmedData.startsWith('data:application/pdf;base64,'))
    return HTML_TYPE.BASE64_PDF
  if (/^JVBER[\s\S]*/.test(trimmedData)) return HTML_TYPE.BASE64_PDF
  if (/<\/?[a-z][\s\S]*>/i.test(trimmedData)) return HTML_TYPE.HTML

  const isLikelyUrl =
    /^https?:\/\/\S+/i.test(trimmedData) ||
    /^[\w\-./% ()]+\/[\w\-./% ()]+\.\w{2,5}$/i.test(trimmedData)

  if (isLikelyUrl) {
    const ext = trimmedData
      .split('?')[0]
      .split('#')[0]
      .split('.')
      .pop()
      ?.toLowerCase()

    switch (ext) {
      case HTML_TYPE.PDF:
        return HTML_TYPE.URL_PDF
      case HTML_TYPE.DOCX:
        return HTML_TYPE.URL_DOCX
    }
  }

  return HTML_TYPE.UNKNOWN
}

export const getOperatorKey = (operator: string, isDynamic?: boolean) => {
  switch (operator) {
    case 'contains':
      return isDynamic
        ? filterOperatorValues.LIKE
        : filterOperatorValues.CONTAINS
    case 'notContains':
      return filterOperatorValues.DOES_NOT_CONTAIN
    case 'equals':
      return filterOperatorValues.EQUALS
    case 'notEqual':
      return filterOperatorValues.NOT_EQUALS
    case 'startsWith':
      return filterOperatorValues.STARTS_WITH
    case 'endsWith':
      return filterOperatorValues.ENDS_WITH
    case 'blank':
      return filterOperatorValues.IS_EMPTY_NULL
    case 'notBlank':
      return filterOperatorValues.IS_NOT_EMPTY_NOT_NULL
    default:
      return operator
  }
}

export const getOperatorFromKey = (key: string) => {
  switch (key) {
    case filterOperatorValues.LIKE:
    case filterOperatorValues.CONTAINS:
      return 'contains'
    case filterOperatorValues.DOES_NOT_CONTAIN:
      return 'notContains'
    case filterOperatorValues.EQUALS:
      return 'equals'
    case filterOperatorValues.NOT_EQUALS:
      return 'notEqual'
    case filterOperatorValues.STARTS_WITH:
      return 'startsWith'
    case filterOperatorValues.ENDS_WITH:
      return 'endsWith'
    case filterOperatorValues.IS_EMPTY_NULL:
      return 'blank'
    case filterOperatorValues.IS_NOT_EMPTY_NOT_NULL:
      return 'notBlank'
    default:
      return key
  }
}

export const isFileSignatureConsistent = async (
  file: File
): Promise<boolean> => {
  const header = await file.slice(0, 2).arrayBuffer()
  const bytes = new Uint8Array(header)
  //  block mz header files
  if (bytes[0] === 0x4d && bytes[1] === 0x5a) {
    return false
  }
  const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? ''
  const detectedFileType = await fileTypeFromBlob(file)
  // block mismatched file types
  if (
    (fileExtension === 'pdf' && detectedFileType?.ext !== 'pdf') ||
    (detectedFileType?.ext === 'pdf' && fileExtension !== 'pdf')
  ) {
    return false
  }
  if (
    (fileExtension === 'png' && detectedFileType?.ext !== 'png') ||
    (detectedFileType?.ext === 'png' && fileExtension !== 'png')
  ) {
    return false
  }
  if (
    (['jpg', 'jpeg'].includes(fileExtension) &&
      detectedFileType?.ext !== 'jpg') ||
    (detectedFileType?.ext === 'jpg' &&
      !['jpg', 'jpeg'].includes(fileExtension))
  ) {
    return false
  }
  if (
    (fileExtension === 'docx' && detectedFileType?.ext !== 'docx') ||
    (detectedFileType?.ext === 'docx' && fileExtension !== 'docx')
  ) {
    return false
  }
  if (
    (fileExtension === 'xlsx' && detectedFileType?.ext !== 'xlsx') ||
    (detectedFileType?.ext === 'xlsx' && fileExtension !== 'xlsx')
  ) {
    return false
  }
  if (
    (fileExtension === 'pptx' && detectedFileType?.ext !== 'pptx') ||
    (detectedFileType?.ext === 'pptx' && fileExtension !== 'pptx')
  ) {
    return false
  }
  if (
    (['doc', 'ppt', 'xls'].includes(fileExtension) &&
      detectedFileType?.ext !== 'cfb') ||
    (detectedFileType?.ext === 'cfb' &&
      !['doc', 'ppt', 'xls'].includes(fileExtension))
  ) {
    return false
  }
  if (
    (fileExtension === 'docm' && detectedFileType?.ext !== 'docm') ||
    (detectedFileType?.ext === 'docm' && fileExtension !== 'docm')
  ) {
    return false
  }
  if (
    (fileExtension === 'xlsm' && detectedFileType?.ext !== 'xlsm') ||
    (detectedFileType?.ext === 'xlsm' && fileExtension !== 'xlsm')
  ) {
    return false
  }
  return true
}

export function validateFileName(fileName: string): ValidateFileNameResultType {
  //nfc normalize file name
  const normalizedFileName = fileName.normalize('NFC')

  //reject file name with exceeding length
  if (normalizedFileName.length > MAX_FILE_NAME_LENGTH) {
    return {
      isValid: false,
      error: FILE_NAME_VALIDATION_ERRORS.LENGTH_EXCEEDED,
    }
  }

  const lastDot = normalizedFileName.lastIndexOf('.')
  const fileBaseName =
    lastDot === -1 ? normalizedFileName : normalizedFileName.slice(0, lastDot)

  // reject if file name is empty or contains only spaces/dots
  if (fileBaseName.length === 0 || /^[. ]+$/.test(fileBaseName)) {
    return {
      isValid: false,
      error: FILE_NAME_VALIDATION_ERRORS.EMPTY_OR_INVALID_NAME,
    }
  }

  // reject file name with leading or trailing spaces
  if (fileBaseName !== fileBaseName.trim()) {
    return {
      isValid: false,
      error: FILE_NAME_VALIDATION_ERRORS.LEADING_TRAILING_SPACE,
    }
  }

  // reject file name with leading or trailing dots
  if (fileBaseName.startsWith('.') || fileBaseName.endsWith('.')) {
    return {
      isValid: false,
      error: FILE_NAME_VALIDATION_ERRORS.LEADING_TRAILING_DOT,
    }
  }

  //reject file name with consecutive dots
  if (fileBaseName.includes('..')) {
    return {
      isValid: false,
      error: FILE_NAME_VALIDATION_ERRORS.CONSECUTIVE_DOTS,
    }
  }

  // reject file name with disallowed characters
  if (!allowedFilenameRegex.test(fileBaseName)) {
    return {
      isValid: false,
      error: FILE_NAME_VALIDATION_ERRORS.DISALLOWED_CHARACTER,
    }
  }

  return { isValid: true }
}

/**
 * Strip Unicode bidirectional / format controls.
 */
export function sanitizeFileNameForDisplay(fileName: string): string {
  return fileName.replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '')
}

export function getFileValidationMessageForUpload(
  errorMessage: string,
  fileName: string
): string {
  return `File [${sanitizeFileNameForDisplay(fileName)}]: ${errorMessage}`
}

export function checkFileExtensionPresent(fileName: string): boolean {
  const lastDot = fileName.lastIndexOf('.')
  const fileExtension = lastDot === -1 ? '' : fileName.slice(lastDot + 1)
  return fileExtension.length > 0
}

export const allowedExtensions = [
  FILE_EXTENSIONS.PDF,
  FILE_EXTENSIONS.DOCX,
  FILE_EXTENSIONS.XLSX,
  FILE_EXTENSIONS.CSV,
  FILE_EXTENSIONS.JPG,
  FILE_EXTENSIONS.JPEG,
  FILE_EXTENSIONS.PNG,
]

export const isValidFileType = (file: File) => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase()
  return (
    fileExtension &&
    (allowedExtensions as readonly string[]).includes(fileExtension)
  )
}

export const getAcceptedExtensionsString = (): string => {
  return allowedExtensions.map((e) => '.' + e).join(', ')
}

export const getUnsupportedFileTypeMessage = (
  fileExtension: string,
  supportedExtensions: string
) =>
  `Unsupported file type "${fileExtension}". Supported extensions: ${supportedExtensions}.`

export function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  const fileExtension =
    lastDot === -1 ? '' : '.' + fileName.slice(lastDot + 1).toLowerCase()
  return fileExtension
}

export function getUploadApiErrorMessage(
  error: unknown,
  fallback = NAMESPACE.UPLOAD_API_ERROR
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined
    if (error.response?.status === 400 && data?.message) {
      return data.message
    }
    if (data?.message) {
      return data.message
    }
  }
  return fallback
}
